import { playCueTone, type CueKind } from './cue';
import { base64ToBytes } from './wav';

export interface QueuedChunk {
  turnId: string;
  index: number;
  text: string;
  /** Cleared after playback ends to free heap on long replies. */
  buffer: AudioBuffer | null;
  source: AudioBufferSourceNode | null;
  startAt: number;
  duration: number;
  started: boolean;
  ended: boolean;
  /** Generation captured when this chunk was accepted; used to ignore stale start marks. */
  generation: number;
}

export interface PlayerEvents {
  onChunkStart: (turnId: string, index: number) => void;
  onChunkEnd: (turnId: string, index: number) => void;
  onIdle: (turnId: string) => void;
}

/**
 * Gapless Web Audio player with instant ducking and heard-position tracking.
 * Buffers are scheduled back-to-back on the AudioContext clock.
 *
 * A monotonic `generation` invalidates in-flight decode after stop/interrupt so late
 * `decodeAudioData` completions cannot resurrect audio.
 */
export class Player {
  ctx: AudioContext;
  private gain: GainNode;
  private analyser: AnalyserNode;
  private analyserBuf: Float32Array<ArrayBuffer>;
  private queue: QueuedChunk[] = [];
  private nextStart = 0;
  private duckLevel = 0.35;
  ducked = false;
  /** Playback frozen (AudioContext suspended) while the arbiter evaluates sustained user speech. */
  paused = false;
  private currentTurn: string | null = null;
  /** Bumped on hard stop; in-flight enqueue after decode must match. */
  private generation = 0;
  /** Soft-yield seal: reject chunks with index > sealedMaxIndex for sealedTurnId. */
  private sealedTurnId: string | null = null;
  private sealedMaxIndex = -1;

  constructor(private events: PlayerEvents) {
    this.ctx = new AudioContext({ latencyHint: 'interactive' });
    this.gain = this.ctx.createGain();
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyserBuf = new Float32Array(this.analyser.fftSize);
    this.gain.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);
  }

  async resume() {
    if (this.ctx.state === 'running' || this.paused) return;
    // Autoplay policy can keep the context suspended until a gesture; never block the pipeline on it.
    await Promise.race([this.ctx.resume().catch(() => {}), new Promise((r) => setTimeout(r, 400))]);
  }

  /** Freeze playback in place (the audio clock stops, so heard-position tracking stays exact). */
  pause() {
    if (this.paused) return;
    this.paused = true;
    // Hard-mute before suspend so browser AEC sees a silent far-end and stops suppressing the user.
    this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.gain.gain.setValueAtTime(0, this.ctx.currentTime);
    void this.ctx.suspend().catch(() => {});
  }

  /** Continue playback from where it was paused. */
  resumePlayback() {
    if (!this.paused) return;
    this.paused = false;
    const level = this.ducked ? this.duckLevel : 1;
    this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.gain.gain.setValueAtTime(level, this.ctx.currentTime);
    void this.ctx.resume().catch(() => {});
  }

  get baseLatencyMs() {
    return (this.ctx.baseLatency ?? 0) * 1000;
  }
  get outputLatencyMs() {
    return ((this.ctx as AudioContext & { outputLatency?: number }).outputLatency ?? 0) * 1000;
  }

  /** RMS of what is currently coming out of the speakers (for the echo guard). */
  outputRms(): number {
    this.analyser.getFloatTimeDomainData(this.analyserBuf);
    let s = 0;
    for (let i = 0; i < this.analyserBuf.length; i++) s += this.analyserBuf[i] * this.analyserBuf[i];
    return Math.sqrt(s / this.analyserBuf.length);
  }

  isPlaying(): boolean {
    return this.queue.some((c) => c.started && !c.ended) || this.queue.some((c) => !c.started);
  }

  /** Audible right now (not paused, and a chunk is scheduled or sounding). */
  isAudible(): boolean {
    return !this.paused && this.isPlaying();
  }

  activeTurn(): string | null {
    // Include paused barge-in freeze — sources are still owned by this turn.
    if (this.paused && this.currentTurn) return this.currentTurn;
    return this.isPlaying() ? this.currentTurn : null;
  }

  /** True when this turn still owns the queue (playing or buffering), even if momentarily idle between clips. */
  isQueueing(turnId: string): boolean {
    return this.currentTurn === turnId && (this.isPlaying() || this.queue.some((c) => !c.ended));
  }

  setDuckLevel(level: number) {
    this.duckLevel = level;
    if (this.ducked) this.gain.gain.setTargetAtTime(level, this.ctx.currentTime, 0.01);
  }

  duck() {
    if (this.ducked) return;
    this.ducked = true;
    this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.gain.gain.setTargetAtTime(this.duckLevel, this.ctx.currentTime, 0.008);
  }

  unduck() {
    if (!this.ducked) return;
    this.ducked = false;
    this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.gain.gain.setTargetAtTime(1, this.ctx.currentTime, 0.04);
  }

  async enqueue(
    turnId: string,
    index: number,
    text: string,
    wav: ArrayBuffer | Uint8Array | string,
    opts?: { format?: 'wav' | 'pcm16'; sampleRate?: number },
  ): Promise<void> {
    const gen = this.generation;
    if (this.isSealedOut(turnId, index)) return;

    await this.resume();
    if (gen !== this.generation) return;

    if (this.currentTurn !== turnId) {
      // New turn: drop unplayed chunks of the previous turn but let the audible sentence finish.
      this.cancelPending();
      this.clearSeal();
      const current = this.queue.find((c) => !c.ended);
      this.queue = current ? [current] : [];
      this.nextStart = current ? current.startAt + current.duration : 0;
      this.currentTurn = turnId;
    }
    if (this.isSealedOut(turnId, index)) return;

    const bytes =
      typeof wav === 'string'
        ? base64ToBytes(wav)
        : wav instanceof ArrayBuffer
          ? new Uint8Array(wav)
          : new Uint8Array(wav.buffer, wav.byteOffset, wav.byteLength);

    let buffer: AudioBuffer;
    if (opts?.format === 'pcm16') {
      const rate = opts.sampleRate ?? 24000;
      const samples = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
      buffer = this.ctx.createBuffer(1, samples.length, rate);
      const channel = buffer.getChannelData(0);
      for (let i = 0; i < samples.length; i++) {
        channel[i] = samples[i] / (samples[i] < 0 ? 0x8000 : 0x7fff);
      }
    } else {
      const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      buffer = await this.ctx.decodeAudioData(ab);
    }

    // Interrupt / seal while decoding: drop without scheduling.
    if (gen !== this.generation || this.isSealedOut(turnId, index)) return;
    if (this.currentTurn !== turnId && this.currentTurn !== null) return;

    const now = this.ctx.currentTime;
    const startAt = Math.max(now + 0.008, this.nextStart);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.gain);
    const chunk: QueuedChunk = {
      turnId,
      index,
      text,
      buffer,
      source,
      startAt,
      duration: buffer.duration,
      started: false,
      ended: false,
      generation: gen,
    };
    this.queue.push(chunk);
    this.nextStart = startAt + buffer.duration;
    source.onended = () => {
      if (chunk.ended) return;
      chunk.ended = true;
      chunk.started = true;
      // Drop heavy PCM after playback ends; keep text for heard-position.
      chunk.buffer = null;
      chunk.source = null;
      this.events.onChunkEnd(turnId, index);
      if (!this.queue.some((c) => !c.ended)) {
        this.unduck();
        this.events.onIdle(turnId);
      }
    };
    source.start(startAt);
    this.scheduleStartMark(chunk);
  }

  /**
   * Fire onChunkStart when the audio clock reaches the chunk. Wall-clock timers keep running while
   * the context is suspended, so re-check against ctx.currentTime and reschedule for the remainder.
   */
  private scheduleStartMark(chunk: QueuedChunk) {
    const delay = Math.max(0, (chunk.startAt - this.ctx.currentTime) * 1000);
    setTimeout(() => {
      if (chunk.started || chunk.ended) return;
      if (chunk.generation !== this.generation) return;
      const remaining = chunk.startAt - this.ctx.currentTime;
      if (remaining > 0.01) {
        // Paused (or clock drift): wait for the audio clock, polling while suspended.
        setTimeout(() => this.scheduleStartMark(chunk), this.paused ? 100 : Math.max(10, remaining * 1000));
        return;
      }
      chunk.started = true;
      this.events.onChunkStart(chunk.turnId, chunk.index);
    }, delay);
  }

  /** Text the user has actually heard so far (finished chunks + proportional part of the current one). */
  heardText(): string {
    const now = this.ctx.currentTime;
    const parts: string[] = [];
    for (const c of this.queue.filter((x) => x.turnId === this.currentTurn)) {
      if (c.ended || now >= c.startAt + c.duration) {
        parts.push(c.text);
      } else if (now > c.startAt) {
        const frac = Math.min(1, (now - c.startAt) / c.duration);
        const words = c.text.trim().split(/\s+/);
        const n = Math.max(0, Math.round(words.length * frac));
        if (n > 0) parts.push(words.slice(0, n).join(' '));
        break;
      } else break;
    }
    return parts.join('').replace(/\s+/g, ' ').trim();
  }

  /** Text through the end of the chunk that is currently audible (for soft yield). */
  heardThroughCurrentChunk(): string {
    const now = this.ctx.currentTime;
    const parts: string[] = [];
    for (const c of this.queue.filter((x) => x.turnId === this.currentTurn)) {
      if (c.startAt <= now || c.ended) parts.push(c.text);
      else break;
    }
    return parts.join('').trim();
  }

  currentChunkText(): string {
    const now = this.ctx.currentTime;
    const c = this.queue.find((x) => !x.ended && x.startAt <= now);
    return c?.text ?? this.queue.find((x) => !x.ended)?.text ?? '';
  }

  currentChunkIndex(): number {
    const now = this.ctx.currentTime;
    const c = this.queue.find((x) => !x.ended && x.startAt <= now);
    return c?.index ?? this.queue.find((x) => !x.ended)?.index ?? -1;
  }

  /** Drop chunks that have not started yet (soft yield: finish the current sentence only). */
  cancelPending() {
    const now = this.ctx.currentTime;
    for (const c of this.queue) {
      if (!c.ended && c.startAt > now + 0.05) {
        try {
          c.source?.stop();
        } catch {
          /* already stopped */
        }
        c.ended = true;
        c.started = true;
      }
    }
    const current = this.queue.find((c) => !c.ended);
    this.nextStart = current ? current.startAt + current.duration : now;
  }

  /**
   * Soft yield: finish the currently audible chunk, drop queued future ones, and reject
   * in-flight decode for later indices of this turn.
   */
  cancelPendingAndSeal(turnId: string) {
    const maxIndex = this.currentChunkIndex();
    this.cancelPending();
    this.sealedTurnId = turnId;
    this.sealedMaxIndex = maxIndex;
  }

  stop() {
    this.generation++;
    this.clearSeal();
    for (const c of this.queue) {
      if (!c.ended) {
        c.ended = true;
        try {
          c.source?.stop();
        } catch {
          /* ignore */
        }
      }
    }
    this.queue = [];
    this.nextStart = 0;
    this.currentTurn = null;
    this.unduck();
    // A paused context must run again so the next turn (or a resumed one) is audible.
    if (this.paused) {
      this.paused = false;
      void this.ctx.resume().catch(() => {});
    }
  }

  private isSealedOut(turnId: string, index: number): boolean {
    return this.sealedTurnId === turnId && index > this.sealedMaxIndex;
  }

  private clearSeal() {
    this.sealedTurnId = null;
    this.sealedMaxIndex = -1;
  }

  /**
   * Soft earcon. Routed straight to the destination so ducking/pause gain does not swallow it.
   * Returns duration in ms.
   * - turn: listening committed / natural end of assistant audio
   * - interrupt: barge-in hard stop
   */
  playCue(kind: CueKind = 'turn'): number {
    void this.resume();
    // A suspended (paused) context cannot audibly schedule; briefly resume for the cue.
    if (this.paused) void this.ctx.resume().catch(() => {});
    return playCueTone(this.ctx, this.ctx.destination, kind);
  }
}
